import React, { useState } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { ActivityIndicator, Text } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { useAppColors } from '../../hooks/useAppColors';
import { useBelts, useDeclareSelfRank } from '../../hooks/useProgression';
import { beltColors } from '../../theme';
import DatePicker from '../DatePicker';

interface Props {
  athleteId: string;
}

/**
 * One-time starting-rank declaration, shown on the Training screen while the
 * athlete has no promotion history. After the first record exists (self-declared
 * or coach-awarded), the backend refuses further self-declarations and this
 * card never shows again.
 */
export default function SetStartingRankCard({ athleteId }: Props) {
  const { t } = useTranslation();
  const colors = useAppColors();
  const { data: belts } = useBelts();
  const declareRank = useDeclareSelfRank();

  const [beltId, setBeltId] = useState<string | null>(null);
  const [stripes, setStripes] = useState(0);
  const [promoDate, setPromoDate] = useState<Date | null>(null);
  const [error, setError] = useState('');

  const handleSubmit = () => {
    if (!beltId) {
      setError(t('training.setRank.beltRequired'));
      return;
    }
    setError('');
    declareRank.mutate(
      {
        athlete_id: athleteId,
        belt_id: beltId,
        stripes,
        // Local date components (not toISOString) — UTC conversion would shift
        // a local midnight back one day in UTC+ timezones like Vietnam
        promotion_date: promoDate
          ? `${promoDate.getFullYear()}-${String(promoDate.getMonth() + 1).padStart(2, '0')}-${String(promoDate.getDate()).padStart(2, '0')}`
          : undefined,
      },
      {
        onError: (err: any) => {
          setError(err?.response?.data?.detail === 'errors.rank_already_set'
            ? t('training.setRank.alreadySet')
            : err.message || t('common.error'));
        },
      },
    );
  };

  return (
    <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.accent }]}>
      <Text style={[styles.title, { color: colors.text }]}>{t('training.setRank.title')}</Text>
      <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
        {t('training.setRank.subtitle')}
      </Text>

      <Text style={[styles.label, { color: colors.textSecondary }]}>{t('training.setRank.belt')}</Text>
      <View style={styles.beltRow}>
        {(belts || []).map((belt: any) => {
          const colorKey = belt.name.includes('.') ? belt.name.split('.').pop() : belt.name;
          const selected = beltId === belt.id;
          return (
            <TouchableOpacity
              key={belt.id}
              style={[
                styles.beltChip,
                { borderColor: selected ? colors.accent : colors.border, backgroundColor: colors.bg },
                selected && { borderWidth: 2 },
              ]}
              onPress={() => { setBeltId(belt.id); if (error) setError(''); }}
              activeOpacity={0.7}
            >
              <View style={[styles.beltDot, { backgroundColor: beltColors[colorKey] || '#ccc', borderColor: colors.border }]} />
              <Text style={[styles.beltChipText, { color: colors.text }]}>{t(belt.name)}</Text>
            </TouchableOpacity>
          );
        })}
      </View>

      <Text style={[styles.label, { color: colors.textSecondary }]}>{t('training.setRank.stripes')}</Text>
      <View style={styles.stripeRow}>
        {[0, 1, 2, 3, 4].map((n) => (
          <TouchableOpacity
            key={n}
            style={[
              styles.stripeChip,
              {
                borderColor: stripes === n ? colors.accent : colors.border,
                backgroundColor: stripes === n ? colors.accent : colors.bg,
              },
            ]}
            onPress={() => setStripes(n)}
            activeOpacity={0.7}
          >
            <Text style={{ color: stripes === n ? colors.accentText : colors.text, fontWeight: '600' }}>{n}</Text>
          </TouchableOpacity>
        ))}
      </View>

      <DatePicker
        label={t('training.setRank.promoDate')}
        value={promoDate}
        onChange={setPromoDate}
        maximumDate={new Date()}
      />

      {!!error && <Text style={[styles.error, { color: colors.error }]}>{error}</Text>}

      <TouchableOpacity
        style={[styles.button, { backgroundColor: colors.accent }]}
        onPress={handleSubmit}
        disabled={declareRank.isPending}
        activeOpacity={0.8}
      >
        {declareRank.isPending ? (
          <ActivityIndicator size="small" color={colors.accentText} />
        ) : (
          <Text style={[styles.buttonText, { color: colors.accentText }]}>{t('training.setRank.submit')}</Text>
        )}
      </TouchableOpacity>
      <Text style={[styles.note, { color: colors.textTertiary }]}>{t('training.setRank.note')}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    borderWidth: 1,
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
  },
  title: { fontSize: 16, fontWeight: '700', marginBottom: 4 },
  subtitle: { fontSize: 13, marginBottom: 12 },
  label: { fontSize: 12, fontWeight: '600', textTransform: 'uppercase', marginBottom: 8, marginTop: 4 },
  beltRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 12 },
  beltChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    borderWidth: 1,
    borderRadius: 20,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  beltDot: { width: 14, height: 14, borderRadius: 7, borderWidth: StyleSheet.hairlineWidth },
  beltChipText: { fontSize: 13, fontWeight: '600' },
  stripeRow: { flexDirection: 'row', gap: 8, marginBottom: 12 },
  stripeChip: {
    width: 44,
    height: 44,
    borderRadius: 22,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  error: { fontSize: 13, textAlign: 'center', marginTop: 8 },
  button: { marginTop: 12, paddingVertical: 14, borderRadius: 12, alignItems: 'center' },
  buttonText: { fontSize: 15, fontWeight: '600' },
  note: { fontSize: 11, textAlign: 'center', marginTop: 8 },
});
